/**
 * Create/edit forms for the expense supporting records: categories, recurring
 * profiles, reimbursement claims and corporate cards.
 *
 * These existed as API endpoints and typed hooks with no UI to reach them, which
 * left the corresponding tabs read-only. Each modal here is mounted only while
 * open and keyed by its target, so form state seeds once from props rather than
 * being reset inside an effect.
 */
import { useMemo, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Badge } from '@/shared/components/ui/Badge';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useSaveExpenseCategory,
  usePostableExpenseAccounts,
  useCreateRecurrence,
  useCreateReimbursement,
  useCreateCard,
  useRecurrenceExecutions,
  useExpensesV2,
  type ExpenseCategory,
  type ExpenseTaxTreatment,
  type ExpenseRecurrence,
} from '../hooks/useExpenseApi';
import { formatMinor, formatShortDate, toMinorUnits, toMajorInput } from '../utils/money';
import { localToday } from '@/shared/utils/utils';

const TAX_TREATMENT_OPTIONS: Array<{ value: ExpenseTaxTreatment; label: string }> = [
  { value: 'exclusive', label: 'Tax exclusive' },
  { value: 'inclusive', label: 'Tax inclusive' },
  { value: 'zero_rated', label: 'Zero rated' },
  { value: 'exempt', label: 'Exempt' },
  { value: 'reverse_charge', label: 'Reverse charge' },
];

// ── Category ────────────────────────────────────────────────────────────────

export interface ExpenseCategoryModalProps {
  open: boolean;
  onClose: () => void;
  category?: ExpenseCategory | null;
}

export function ExpenseCategoryModal({ open, onClose, category }: ExpenseCategoryModalProps) {
  const isEdit = !!category;
  const save = useSaveExpenseCategory();
  const accounts = usePostableExpenseAccounts();

  // '' means "resolve from the category code via the built-in map".
  const accountOptions = useMemo(
    () => [
      { value: '', label: 'Resolve automatically from the category code' },
      ...(accounts.data ?? []).map((a) => ({ value: a.code, label: `${a.code} — ${a.name}` })),
    ],
    [accounts.data],
  );

  const [form, setForm] = useState(() => ({
    code: category?.code ?? '',
    name: category?.name ?? '',
    expenseAccountCode: category?.expenseAccountCode ?? '',
    defaultTaxRate: String(category?.defaultTaxRate ?? 18),
    defaultTaxTreatment: (category?.defaultTaxTreatment ?? 'exclusive') as ExpenseTaxTreatment,
    maxAmount: toMajorInput(category?.maxAmountMinor),
    requiresReceipt: !!category?.requiresReceipt,
    isBillableByDefault: !!category?.isBillableByDefault,
    isReimbursableByDefault: !!category?.isReimbursableByDefault,
    isCapital: !!category?.isCapital,
  }));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // Mirrors the server's allowlist so the user gets immediate feedback.
  const error = useMemo(() => {
    if (!form.name.trim()) return 'A category name is required.';
    if (!isEdit && !/^[a-z0-9_-]{1,40}$/.test(form.code.trim().toLowerCase())) {
      return 'Code may use only lowercase letters, digits, hyphen and underscore.';
    }
    const rate = Number(form.defaultTaxRate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) return 'Tax rate must be between 0 and 100.';
    return null;
  }, [form, isEdit]);

  const submit = () => {
    if (error) { toast.error(error); return; }
    save.mutate(
      {
        id: category?._id,
        payload: {
          code: form.code.trim().toLowerCase(),
          name: form.name.trim(),
          expenseAccountCode: form.expenseAccountCode.trim() || undefined,
          defaultTaxRate: Number(form.defaultTaxRate) || 0,
          defaultTaxTreatment: form.defaultTaxTreatment,
          maxAmountMinor: toMinorUnits(form.maxAmount),
          requiresReceipt: form.requiresReceipt,
          isBillableByDefault: form.isBillableByDefault,
          isReimbursableByDefault: form.isReimbursableByDefault,
          isCapital: form.isCapital,
        } as Partial<ExpenseCategory> & { expenseAccountCode?: string },
      },
      {
        onSuccess: () => { toast.success(isEdit ? 'Category updated' : 'Category created'); onClose(); },
        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not save the category'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${category?.name}` : 'New expense category'}
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">{error ?? 'Categories set the GL account and defaults.'}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending || !!error}>{isEdit ? 'Save' : 'Create'}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            disabled={isEdit}
            placeholder="travel"
            hint={isEdit ? 'Code cannot change once expenses reference it' : 'Lowercase, no spaces'}
          />
          <Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Travel & Conveyance" />
          {/* A picker, not free text: only accounts that can actually receive a
              posting are offered, so a code the server would reject cannot be
              typed. Falls back to manual entry if the chart cannot be read. */}
          {accountOptions.length > 0 ? (
            <div>
              <Select
                label="GL account (optional)"
                value={form.expenseAccountCode}
                onChange={(e) => set('expenseAccountCode', e.target.value)}
                options={accountOptions}
              />
              <p className="mt-1 text-xs text-slate-500">
                Leave as “Resolve automatically” to map from the category code.
              </p>
            </div>
          ) : (
            <Input
              label="GL account code (optional)"
              value={form.expenseAccountCode}
              onChange={(e) => set('expenseAccountCode', e.target.value)}
              placeholder="6120"
              hint={
                accounts.isLoading
                  ? 'Loading available accounts…'
                  : 'No expense accounts found — seed the chart of accounts, or leave blank to resolve automatically.'
              }
            />
          )}
          <Input
            type="number"
            step="0.01"
            min="0"
            label="Spend ceiling (optional)"
            value={form.maxAmount}
            onChange={(e) => set('maxAmount', e.target.value)}
            hint="Blank means no cap. This is a rupee amount, per claim."
          />
          <Input
            type="number"
            step="0.01"
            min="0"
            max="100"
            label="Default tax rate %"
            value={form.defaultTaxRate}
            onChange={(e) => set('defaultTaxRate', e.target.value)}
          />
          <Select
            label="Default tax treatment"
            value={form.defaultTaxTreatment}
            onChange={(e) => set('defaultTaxTreatment', e.target.value as ExpenseTaxTreatment)}
            options={TAX_TREATMENT_OPTIONS}
          />
        </div>

        <div className="flex flex-wrap gap-4 pt-1">
          {([
            ['requiresReceipt', 'Receipt required'],
            ['isBillableByDefault', 'Billable by default'],
            ['isReimbursableByDefault', 'Reimbursable by default'],
            ['isCapital', 'Capital purchase'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form[key]} onChange={(e) => set(key, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ── Recurrence ──────────────────────────────────────────────────────────────

export interface RecurrenceModalProps {
  open: boolean;
  onClose: () => void;
}

export function RecurrenceModal({ open, onClose }: RecurrenceModalProps) {
  const create = useCreateRecurrence();

  const [form, setForm] = useState(() => ({
    name: '',
    frequency: 'monthly',
    startDate: localToday(),
    endDate: '',
    dayOfPeriod: String(new Date().getUTCDate()),
    maxOccurrences: '',
    amount: '',
    taxRate: '18',
    categoryLabel: '',
    description: '',
    paymentMethod: 'bank_transfer',
  }));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const error = useMemo(() => {
    if (!form.name.trim()) return 'A profile name is required.';
    if (toMinorUnits(form.amount) <= 0) return 'Enter an amount greater than zero.';
    if (!form.startDate) return 'A start date is required.';
    if (form.endDate && form.endDate < form.startDate) return 'End date cannot precede the start date.';
    return null;
  }, [form]);

  const submit = () => {
    if (error) { toast.error(error); return; }
    create.mutate(
      {
        name: form.name.trim(),
        frequency: form.frequency,
        startDate: form.startDate,
        endDate: form.endDate || undefined,
        dayOfPeriod: Number(form.dayOfPeriod) || 1,
        maxOccurrences: Number(form.maxOccurrences) || 0,
        template: {
          expenseDate: form.startDate,
          netAmountMinor: toMinorUnits(form.amount),
          taxRate: Number(form.taxRate) || 0,
          categoryLabel: form.categoryLabel || undefined,
          description: form.description || undefined,
          paymentMethod: form.paymentMethod,
        },
      },
      {
        onSuccess: () => { toast.success('Recurring profile created'); onClose(); },
        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not create the profile'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="New recurring expense"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {error ?? 'Each occurrence generates a draft that still needs approval.'}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
            <Button onClick={submit} disabled={create.isPending || !!error}>Create profile</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Profile name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Monthly office rent" />
          <Select
            label="Frequency"
            value={form.frequency}
            onChange={(e) => set('frequency', e.target.value)}
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
          />
          <Input type="date" label="Starts" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
          <Input type="date" label="Ends (optional)" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
          <Input
            type="number"
            min="1"
            max="31"
            label="Day of period (optional)"
            value={form.dayOfPeriod}
            onChange={(e) => set('dayOfPeriod', e.target.value)}
            hint="Month-end is clamped automatically"
          />
          <Input
            type="number"
            min="0"
            label="Max occurrences (optional)"
            value={form.maxOccurrences}
            onChange={(e) => set('maxOccurrences', e.target.value)}
            hint="0 or blank means unlimited"
          />
        </div>

        <p className="pt-1 text-xs font-medium uppercase tracking-wide text-slate-500">Expense template</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input type="number" step="0.01" min="0" label="Amount (before tax)" value={form.amount} onChange={(e) => set('amount', e.target.value)} />
          <Input type="number" step="0.01" min="0" max="100" label="Tax rate %" value={form.taxRate} onChange={(e) => set('taxRate', e.target.value)} />
          <Input label="Category label (optional)" value={form.categoryLabel} onChange={(e) => set('categoryLabel', e.target.value)} placeholder="rent" />
          <Input label="Description (optional)" value={form.description} onChange={(e) => set('description', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

// ── Recurrence execution log ────────────────────────────────────────────────

export interface RecurrenceExecutionsModalProps {
  open: boolean;
  onClose: () => void;
  recurrence: ExpenseRecurrence;
}

export function RecurrenceExecutionsModal({ open, onClose, recurrence }: RecurrenceExecutionsModalProps) {
  const { data, isLoading } = useRecurrenceExecutions(recurrence._id);
  const rows = data?.items ?? [];

  return (
    <Modal open={open} onClose={onClose} size="lg" title={`Run history — ${recurrence.name}`}>
      {isLoading ? (
        <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">This profile has not run yet.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200">
            <tr>
              <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Occurrence</th>
              <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Ran at</th>
              <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Attempt</th>
              <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="border-b border-slate-100">
                <td className="px-2 py-1.5 font-mono text-xs">{r.periodKey}</td>
                <td className="px-2 py-1.5 text-xs">{formatShortDate(r.runAt)}</td>
                <td className="px-2 py-1.5 text-xs">{r.attempt}</td>
                <td className="px-2 py-1.5">
                  <Badge tone={r.outcome === 'created' ? 'success' : r.outcome === 'failed' ? 'danger' : 'neutral'}>
                    {r.outcome}
                  </Badge>
                  {r.error ? <p className="mt-0.5 text-xs text-rose-600">{r.error}</p> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-3 text-xs text-slate-500">
        One row per occurrence. A unique constraint on the occurrence key is what makes duplicate
        generation impossible, even under retries.
      </p>
    </Modal>
  );
}

// ── Reimbursement claim ─────────────────────────────────────────────────────

export interface ReimbursementModalProps {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName?: string;
}

export function ReimbursementModal({ open, onClose, employeeId, employeeName }: ReimbursementModalProps) {
  const create = useCreateReimbursement();
  // Only employee-funded, posted, still-unpaid expenses can be reimbursed.
  const { data, isLoading } = useExpensesV2({
    employeeId,
    settlementMode: 'employee_paid',
    paymentStatus: 'unpaid',
    pageSize: 100,
  });

  const candidates = (data?.items ?? []).filter(
    (e) => (e.outstandingMinor > 0 || e.grossAmountMinor > 0) && e.paymentStatus !== 'paid' && ['posted', 'approved', 'submitted'].includes(e.status)
  );
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const total = candidates
    .filter((e) => picked.has(e._id))
    .reduce((sum, e) => sum + e.outstandingMinor, 0);

  const submit = () => {
    if (picked.size === 0) { toast.error('Select at least one expense'); return; }
    create.mutate(
      { employeeId, expenseIds: [...picked] },
      {
        onSuccess: () => { toast.success('Reimbursement claim created'); onClose(); },
        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not create the claim'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Reimbursement claim — ${employeeName || employeeId}`}
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-slate-600">
            {picked.size} selected · <span className="font-medium">{formatMinor(total)}</span>
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
            <Button onClick={submit} disabled={create.isPending || picked.size === 0}>Create claim</Button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
      ) : candidates.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Nothing outstanding for this employee. Only posted, employee-funded expenses can be reimbursed.
        </p>
      ) : (
        <>
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={picked.size === candidates.length}
              onChange={(e) => setPicked(e.target.checked ? new Set(candidates.map((c) => c._id)) : new Set())}
              aria-label="Select all outstanding expenses"
            />
            Select all
          </label>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200">
              <tr>
                <th className="px-2 py-1.5" />
                <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Expense</th>
                <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Date</th>
                <th className="px-2 py-1.5 text-xs uppercase tracking-wide text-slate-500">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((e) => (
                <tr key={e._id} className="border-b border-slate-100">
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={picked.has(e._id)}
                      onChange={() =>
                        setPicked((prev) => {
                          const n = new Set(prev);
                          if (n.has(e._id)) n.delete(e._id); else n.add(e._id);
                          return n;
                        })
                      }
                      aria-label={`Select ${e.expenseNo}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">{e.expenseNo}</td>
                  <td className="px-2 py-1.5 text-xs">{formatShortDate(e.expenseDate)}</td>
                  <td className="px-2 py-1.5 font-medium">{formatMinor(e.outstandingMinor, e.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

// ── Corporate card ──────────────────────────────────────────────────────────

export interface CorporateCardModalProps {
  open: boolean;
  onClose: () => void;
}

export function CorporateCardModal({ open, onClose }: CorporateCardModalProps) {
  const create = useCreateCard();

  const [form, setForm] = useState(() => ({
    cardName: '',
    lastFourDigits: '',
    issuer: '',
    holderName: '',
    creditLimit: '',
  }));

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const error = !form.cardName.trim() ? 'A card name is required.' : null;

  const submit = () => {
    if (error) { toast.error(error); return; }
    create.mutate(
      {
        cardName: form.cardName.trim(),
        lastFourDigits: form.lastFourDigits.replace(/\D/g, '').slice(-4),
        issuer: form.issuer.trim(),
        holderName: form.holderName.trim(),
        creditLimitMinor: toMinorUnits(form.creditLimit),
      },
      {
        onSuccess: () => { toast.success('Corporate card registered'); onClose(); },
        onError: (e: unknown) => toast.error(getApiErrorMessage(e) || 'Could not register the card'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Register corporate card"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">{error ?? 'Only the last four digits are stored.'}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>Cancel</Button>
            <Button onClick={submit} disabled={create.isPending || !!error}>Register</Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Card name" value={form.cardName} onChange={(e) => set('cardName', e.target.value)} placeholder="HDFC Platinum" />
        <Input
          label="Last 4 digits (optional)"
          value={form.lastFourDigits}
          onChange={(e) => set('lastFourDigits', e.target.value)}
          maxLength={4}
          inputMode="numeric"
          hint="Never store a full card number"
        />
        <Input label="Issuer (optional)" value={form.issuer} onChange={(e) => set('issuer', e.target.value)} />
        <Input label="Holder name (optional)" value={form.holderName} onChange={(e) => set('holderName', e.target.value)} />
        <Input
          type="number"
          step="0.01"
          min="0"
          label="Credit limit (optional)"
          value={form.creditLimit}
          onChange={(e) => set('creditLimit', e.target.value)}
        />
      </div>
    </Modal>
  );
}
